import React, { useState, useEffect } from "react";
import styles from "../../../styles/ChordProgression.module.css";
import { ChordProgressionQuizOptions } from "./ChordProgressionQuiz";
import { Note } from "../../../soundLibrary";
import ModeSelector, { QuizMode } from "../../../components/ModeSelector";
import Tooltip from "../../../components/Tooltip";
import { getDegreeReference } from "../../../data/progressionReferences";

interface Props {
    setQuizOptions: (opts: ChordProgressionQuizOptions) => void;
    initialOptions?: ChordProgressionQuizOptions;
}

// Diatonic chords in major scale: I, ii, iii, IV, V, vi, vii°
const DIATONIC_CHORDS = [1, 2, 3, 4, 5, 6, 7];
const CHORD_LABELS = ['I', 'ii', 'iii', 'IV', 'V', 'vi', 'vii°'];
const PROGRESSION_LENGTHS = [3, 4];

const availableKeys: Note[] = ["C4" as Note, "G4" as Note, "D4" as Note, "A4" as Note, "E4" as Note];

export default function ChordProgressionPreQuiz({ setQuizOptions, initialOptions }: Props) {
    const [selectedChords, setSelectedChords] = useState<number[]>(
        initialOptions?.selectedChords ?? [1, 2, 4, 5, 6]
    );
    const [progressionLength, setProgressionLength] = useState<number>(initialOptions?.progressionLength ?? 4);
    const [mode, setMode] = useState<QuizMode>(initialOptions?.mode ?? "questions");
    const [duration, setDuration] = useState<number>(initialOptions?.timeLimitSeconds ?? 60);
    const [questionLimit, setQuestionLimit] = useState<number>(initialOptions?.questionLimit ?? 6);
    const [difficulty, setDifficulty] = useState<number>(initialOptions?.difficulty ?? 1);
    const [keyNote, setKeyNote] = useState<Note>(initialOptions?.keyNote ?? ("C4" as Note));

    useEffect(() => {
        setQuizOptions({
            selectedChords,
            progressionLength,
            keyNote,
            mode,
            timeLimitSeconds: mode === "time" ? duration : undefined,
            questionLimit: mode === "questions" ? questionLimit : undefined,
            difficulty,
        });
    }, [selectedChords, progressionLength, mode, duration, questionLimit, difficulty, keyNote, setQuizOptions]);

    const toggleChord = (chord: number) => {
        setSelectedChords((prev) =>
            prev.includes(chord)
                ? prev.filter((c) => c !== chord)
                : [...prev, chord].sort((a, b) => a - b)
        );
    };

    const toggleAllChords = () => {
        if (selectedChords.length === DIATONIC_CHORDS.length) {
            setSelectedChords([]);
        } else {
            setSelectedChords([...DIATONIC_CHORDS]);
        }
    };

    return (
        <div className={styles.preQuizWrapper}>
            <ModeSelector value={mode} onChange={setMode} />

            {mode === "time" && (
                <div className={styles.drillOptionLabel}>
                    <label className={styles.comboLabel}>
                        Duration: {" "}
                        <input
                            type="number"
                            value={duration}
                            min={10}
                            max={600}
                            onChange={(e) => setDuration(Number(e.target.value))}
                            className={styles.preQuizCounter}
                        />
                    </label>
                </div>
            )}

            {mode === "questions" && (
                <div className={styles.drillOptionLabel}>
                    <label className={styles.comboLabel}>
                        Question limit: {" "}
                        <input
                            type="number"
                            value={questionLimit}
                            min={1}
                            max={100}
                            onChange={(e) => setQuestionLimit(Number(e.target.value))}
                            className={styles.preQuizCounter}
                        />
                    </label>
                </div>
            )}

            <div className={styles.drillOptionLabel}>
                <label className={styles.comboLabel}>
                    Progression Length:{" "}
                    <select
                        value={progressionLength}
                        onChange={(e) => setProgressionLength(Number(e.target.value))}
                        className={styles.preQuizCombo}
                    >
                        {PROGRESSION_LENGTHS.map((length) => (
                            <option key={length} value={length}>
                                {length} chords
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className={styles.drillOptionLabel}>
                <label className={styles.comboLabel}>
                    Key: {" "}
                    <select value={keyNote} onChange={(e) => setKeyNote(e.target.value as Note)} className={styles.preQuizCombo}>
                        {availableKeys.map((k) => (
                            <option key={k} value={k}>{k}</option>
                        ))}
                    </select>
                </label>
            </div>

            <div className={styles.drillOptionLabel}>
                <label className={styles.comboLabel}>
                    <Tooltip
                        content="Difficulty determines how similar the answer choices are. Easy has very different answers, while Hard has very similar answers."
                        position="top"
                    >
                        <span style={{ cursor: "help" }}>Difficulty:</span>
                    </Tooltip>
                    {" "}
                    <select
                        value={difficulty}
                        onChange={(e) => setDifficulty(Number(e.target.value))}
                        className={styles.preQuizCombo}
                    >
                        <option value={1}>Easy</option>
                        <option value={2}>Medium</option>
                        <option value={3}>Hard</option>
                    </select>
                </label>
            </div>

            <div className={styles.selectAllWrapper} style={{ marginTop: "1rem" }}>
                <div>Diatonic Chords:</div>
                <button onClick={toggleAllChords} className={styles.preQuizButton}>
                    {selectedChords.length === DIATONIC_CHORDS.length ? "Deselect All" : "Select All"}
                </button>
            </div>

            <div className={styles.chordsWrapper}>
                {DIATONIC_CHORDS.map((chord, index) => {
                    const ref = getDegreeReference(chord);
                    const tooltipContent = ref ? (
                        <div className={styles.degreeHint}>
                            <div className={styles.degreeHintName}>{ref.name}</div>
                            <div className={styles.degreeHintFunction}>{ref.function}</div>
                            <div className={styles.degreeHintCharacter}>{ref.character}</div>
                        </div>
                    ) : null;

                    return (
                        <Tooltip key={chord} content={tooltipContent} position="top">
                            <button
                                onClick={() => toggleChord(chord)}
                                className={
                                    selectedChords.includes(chord)
                                        ? `${styles.chordCheckboxWrapper} ${styles.chordCheckboxWrapperChecked}`
                                        : styles.chordCheckboxWrapper
                                }
                            >
                                {CHORD_LABELS[index]}
                            </button>
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );
}
